import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CategoryTabs from './components/CategoryTabs';
import VideoGrid from './components/VideoGrid';
import { videos, categories } from '@/mocks/videos';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredVideos = useMemo(() => {
    if (activeCategory === 'all') return videos;
    return videos.filter((v) => v.category === activeCategory);
  }, [activeCategory]);

  const hotVideos = useMemo(() => videos.filter((v) => v.isHot), []);
  const newVideos = useMemo(() => videos.filter((v) => v.isNew), []);
  const featuredVideos = useMemo(() => videos.filter((v) => v.featured), []);
  const moviesOnly = useMemo(() => videos.filter((v) => v.category === 'movie'), []);
  const tvOnly = useMemo(() => videos.filter((v) => v.category === 'tv'), []);
  const varietyOnly = useMemo(() => videos.filter((v) => v.category === 'variety'), []);

  const allCategories = [{ slug: 'all', label: '全部', icon: 'ri-apps-line' }, ...categories];

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '91视频',
    url: 'https://www.91video.com',
    description: '91视频网站电影电视剧综艺频道，免费在线观看91电影视频、电视剧全集、热门综艺，高清不缓冲。',
    inLanguage: 'zh-CN',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.91video.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91视频',
    url: 'https://www.91video.com',
    description: '国内领先的免费在线视频平台，提供海量电影、电视剧、综艺节目免费在线观看。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@91video.com',
      telephone: '+86-400-123-4567',
      contactType: 'customer service',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <Navbar transparent />

      <main>
        <HeroSection />

        <section className="w-full px-4 md:px-8 max-w-7xl mx-auto py-8 md:py-12">
          <div className="mb-6">
            <CategoryTabs
              categories={allCategories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
            />
          </div>

          {activeCategory === 'all' ? (
            <>
              {featuredVideos.length > 0 && (
                <div className="mb-10">
                  <VideoGrid videos={featuredVideos} title="精选推荐" subtitle="编辑精选，不容错过的好内容" />
                </div>
              )}

              {hotVideos.length > 0 && (
                <div className="mb-10">
                  <VideoGrid videos={hotVideos} title="热门推荐" subtitle="全网热播，大家都在看" />
                </div>
              )}

              <div className="mb-10">
                <VideoGrid videos={moviesOnly} title="热映电影" subtitle="院线大片，在线免费观看" />
              </div>

              <div className="mb-10">
                <VideoGrid videos={tvOnly} title="热门电视剧" subtitle="追剧必备，精彩不断" />
              </div>

              <div className="mb-10">
                <VideoGrid videos={varietyOnly} title="热门综艺" subtitle="欢乐时光，爆笑不断" />
              </div>

              {newVideos.length > 0 && (
                <div className="mb-10">
                  <VideoGrid videos={newVideos} title="最新上线" subtitle="每日更新，新鲜速递" />
                </div>
              )}
            </>
          ) : (
            <VideoGrid
              videos={filteredVideos}
              title={categories.find((c) => c.slug === activeCategory)?.label || '分类浏览'}
              subtitle={`共 ${filteredVideos.length} 部作品`}
            />
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}